import { useParams, Navigate, Link } from "react-router-dom";
import { SITE_URL } from "@/config/site";
import { useTranslation } from "react-i18next";
import { useLocalePrefix } from "@/hooks/useLocalePrefix";
import { ArrowRight, Check, Cpu, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import PageLayout from "@/components/PageLayout";

// Programmatic per-model landing page (/models/:slug, /:lng/models/:slug).
// i18n-driven (models.* namespace, 15 languages); name/developer/family/context/
// providers are invariant, tagline/description/strengths/bestFor/kind/params
// translate. Prerendered by scripts/prerender-seo.js + sitemap (same-slug
// cross-locale hreflang clusters).
const ModelPage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const localePrefix = useLocalePrefix();
  const rawModels = t("models.list", { returnObjects: true });
  const models = Array.isArray(rawModels) ? rawModels : [];
  const model = models.find((m) => m.slug === slug);
  if (!model) return <Navigate to={`${localePrefix}/models`} replace />;

  const related = models
    .filter((m) => m.family === model.family && m.slug !== model.slug)
    .slice(0, 3);

  const providers = model.providers.join(", ");
  const title = t("models.detailTitle", {
    name: model.name,
    developer: model.developer,
  });
  const description = t("models.detailDesc", {
    description: model.description,
    name: model.name,
    count: model.providers.length,
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: t("models.crumbHome"),
            item: `${SITE_URL}${localePrefix}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t("models.crumbModels"),
            item: `${SITE_URL}${localePrefix}/models`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: model.name,
            item: `${SITE_URL}${localePrefix}/models/${model.slug}`,
          },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: t("models.faqFreeQ", { name: model.name }),
            acceptedAnswer: {
              "@type": "Answer",
              text: t("models.faqFreeA", { name: model.name, providers }),
            },
          },
          {
            "@type": "Question",
            name: t("models.faqWhoQ", { name: model.name }),
            acceptedAnswer: {
              "@type": "Answer",
              text: t("models.faqWhoA", {
                name: model.name,
                developer: model.developer,
                tagline: model.tagline,
              }),
            },
          },
          {
            "@type": "Question",
            name: t("models.faqWhatQ", { name: model.name }),
            acceptedAnswer: {
              "@type": "Answer",
              text: t("models.faqWhatA", {
                name: model.name,
                bestFor: model.bestFor.join(", "),
              }),
            },
          },
        ],
      },
    ],
  };

  return (
    <PageLayout>
      <SEO
        title={title}
        description={description}
        canonical={`${localePrefix}/models/${model.slug}`}
        jsonLd={jsonLd}
      />
      <article className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <nav
            className="flex items-center gap-1 text-sm text-slate-400 mb-8"
            aria-label="Fil d'Ariane"
          >
            <Link to={`${localePrefix}/`} className="hover:text-primary">
              {t("models.crumbHome")}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`${localePrefix}/models`} className="hover:text-primary">
              {t("models.crumbModels")}
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{model.name}</span>
          </nav>

          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <Cpu className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
            {model.name}
          </h1>
          <p className="text-xl text-slate-300 mb-8">{model.tagline}</p>

          <div className="flex flex-wrap gap-2 mb-10">
            {[
              [t("models.specEditor"), model.developer],
              [t("models.specType"), model.kind],
              [t("models.specParams"), model.params],
              [t("models.specContext"), model.context],
            ].map(([label, value]) => (
              <span
                key={label}
                className="text-sm glass rounded-lg px-3 py-1.5 text-slate-300"
              >
                <span className="text-slate-500">{label} : </span>
                {value}
              </span>
            ))}
          </div>

          <p className="text-slate-300 leading-relaxed mb-10">
            {model.description}
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            <div className="glass rounded-2xl p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">
                {t("models.strengthsTitle")}
              </h2>
              <ul className="space-y-2">
                {model.strengths.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-2 text-slate-300 text-sm"
                  >
                    <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="glass rounded-2xl p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">
                {t("models.bestForTitle")}
              </h2>
              <ul className="space-y-2">
                {model.bestFor.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-slate-300 text-sm"
                  >
                    <ArrowRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 mb-12">
            <h2 className="font-heading text-lg font-semibold text-white mb-2">
              {t("models.availTitle")}
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              {t("models.availNote", { name: model.name, providers })}
            </p>
            <Link to="/register">
              <Button className="btn-glow bg-primary-strong hover:bg-primary-strong/90">
                {t("models.tryBtn", { name: model.name })}{" "}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {related.length > 0 && (
            <div className="mb-10">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">
                {t("models.relatedTitle", { family: model.family })}
              </h2>
              <div className="flex flex-wrap gap-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`${localePrefix}/models/${r.slug}`}
                    className="glass rounded-lg px-4 py-2 text-sm text-slate-300 hover:text-primary hover:border-primary/30 transition-all"
                  >
                    {r.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <p className="text-slate-400 text-sm">
            <Link
              to={`${localePrefix}/models`}
              className="text-primary underline underline-offset-2"
            >
              ← {t("models.backToList")}
            </Link>{" "}
            ·{" "}
            <Link
              to={`${localePrefix}/compare`}
              className="text-primary underline underline-offset-2"
            >
              {t("models.compareLink")}
            </Link>{" "}
            ·{" "}
            <Link
              to={`${localePrefix}/pricing`}
              className="text-primary underline underline-offset-2"
            >
              {t("models.pricing")}
            </Link>
          </p>
        </div>
      </article>
    </PageLayout>
  );
};

export default ModelPage;
